import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {WorkspaceUiConfigConfiguration} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceUiConfigConfiguration";
import {mergeTransferModuleIntoManifest} from "~/domainModels/transfer/transferManifestPatch";
import type {WorkspaceUiConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceUiConfig";
import {getOptionalWorkspaceUiConfigConfiguration} from "~/hooks/workspaceConfiguration";
import {
    createBuiltinDataSourcesRecord,
    getBuiltinDataSource,
} from "./configDataSourceRegistry";
import {
    DataSourceKind,
    PageType,
    attachPageFieldBindings,
    columnMappingEntityKind,
    enrichColumnMappingFormatters,
    findFormPageByFormId,
    flattenCardDetailCells,
    isAgentHomePage,
    isCtaTableAction,
    isDashboardPage,
    isDispositionTrackerPage,
    isFormPage,
    isDuluxMyDayPlanPage,
    isHostedPage,
    isHostedPageType,
    isLeaderboardPage,
    isFrontendLeaderboardPage,
    isJourneyProfileComponent,
    isJourneyProfilePage,
    isListStylePage,
    isUserInvitationPage,
    isUsersPage,
    parseColumnMappingEntity,
    resolveFilterItemValues,
    resolveFrontendFieldIdToColumn,
    formSectionHasFieldRows,
    WORKSPACE_APP_ID,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import type {
    AgentHomePage,
    AgentViewPage,
    AgentViewProfilePanel as ConfigAgentViewProfilePanel,
    CardDetailCell,
    CardEventsBar,
    CardLayout,
    ColumnMapping,
    Cta,
    DashboardPage,
    DataSource,
    DuluxAgentHomePage,
    DuluxAgentTimelinessConfig,
    DuluxMyDayPlanPage,
    DispositionTrackerPage,
    ConfigComponent,
    EventContainerWithEventsPage,
    EventPage,
    EventWithEventContainerPage,
    FieldFormatter,
    FieldOption,
    FieldOptionSource,
    FieldType as ConfigFieldType,
    FilterItem,
    Form,
    FormField,
    FormPage,
    HostedPage,
    JourneyProfilePage,
    LeaderboardPage,
    FrontendLeaderboardPage,
    ListPage,
    NavItem,
    PageView,
    PageViewEngine,
    Scaffold,
    ScaffoldApp,
    ScaffoldBottomBar,
    ScaffoldByUserTemplate,
    SidebarType,
    SortItem,
    Resolver,
    WorkspaceConfig,
    WorkspacePage,
    WorkspaceTransferUiConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {resolveFirstSidebarPageKey} from "./scaffoldNavigation";
function pageKind(page: {type: string}): string {
    return page.type;
}

function isNonListPageKind(kind: string): boolean {
    return (
        kind === PageType.FormPage ||
        kind === PageType.AgentHomePage ||
        kind === PageType.DuluxAgentHomePage ||
        kind === PageType.WorkspaceBrandingHome ||
        kind === PageType.DashBoard ||
        kind === PageType.Leaderboard ||
        kind === PageType.FrontendLeaderboard ||
        kind === PageType.DispositionTracker ||
        kind === PageType.JourneyProfile ||
        kind === PageType.DuluxBlueStoreQuotations ||
        // Hosted shells (Users, data connections, Products, Reports, …) must stay in
        // runtime `pages` or `/w/$workspaceId/$pageKey` beforeLoad 404s.
        isHostedPageType(kind) === true
    );
}

function oneMonthAgoIso(): string {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    return now.toISOString();
}

/** Legacy page-embedded data source accepted until the stored config migration runs. */
type LegacyEmbeddedDataSource = {
    adapter: string;
    eventTemplateId?: Uuid;
    defaultSort?: Array<SortItem>;
    operations?: unknown;
};

export function getDataSource(dataSourceId: Uuid): DataSource | undefined {
    return getBuiltinDataSource(dataSourceId);
}

/** @deprecated Use {@link getDataSource} — kept for call-site compatibility. */
export function getHarnessDataSource(dataSourceId: Uuid): DataSource | undefined {
    return getDataSource(dataSourceId);
}

type ManifestFieldOption = {
    label: string;
    backgroundColorToken?: string | null;
    textColorToken?: string | null;
    borderColorToken?: string | null;
};

/** Raw field entry in architect JSON before {@link normalizeManifestField}. */
type ManifestFieldRaw = {
    id: Uuid;
    label: string;
    /** @deprecated Prefer `formatter`. Accepted while drafts migrate. */
    customFormatter?: FieldFormatter;
    formatter?: FieldFormatter;
    optionSource?: FieldOptionSource;
    options?: Record<string, ManifestFieldOption> | null;
    resolverId?: Uuid | null;
    defaultValue?: string | boolean | number | null;
    isVisible?: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    requiredWhen?: ConfigFieldType["requiredWhen"];
    readonlyWhen?: ConfigFieldType["readonlyWhen"];
    visibleWhen?: ConfigFieldType["visibleWhen"];
    errorMessage?: string | null;
    validation?: unknown;
    emptyLabel?: string | null;
};

type ManifestListPage = {
    allowedUserTemplateIds?: Array<Uuid>;
    readOnly?: boolean;
    id: Uuid;
    title: string;
    type: string;
    /** When true, persist filters/sort/column layout in localStorage. Omitted/null → false. */
    persistInLocalStorage?: boolean | null;
    archivedPageId?: Uuid | null;
    parentActivePageId?: Uuid | null;
    /** @deprecated Prefer `dataSourceId` + page fields. */
    dataSource?: LegacyEmbeddedDataSource;
    dataSourceId?: Uuid;
    eventTemplateId?: Uuid;
    eventContainerTemplateId?: Uuid;
    dispositionProviderEventContainerTemplateId?: Uuid;
    defaultSort?: Array<SortItem>;
    profilePanel?: Partial<ConfigAgentViewProfilePanel> &
        Pick<ConfigAgentViewProfilePanel, "viewFormId" | "footerToolbarCtaIds" | "footerMoreCtaIds">;
    filters: ListPage["filters"];
    mandatoryFilters?: Array<FilterItem> | null;
    fields: Array<ManifestFieldRaw>;
    viewEngine: PageViewEngine;
    domainAggregateTemplateId?: Uuid;
    timeliness?: DuluxAgentTimelinessConfig;
};

type ManifestDispositionTrackerPage = Omit<DispositionTrackerPage, "dataSourceId"> & {
    dataSource?: LegacyEmbeddedDataSource;
    dataSourceId?: Uuid;
};

type ManifestPage =
    | ManifestListPage
    | AgentHomePage
    | DuluxAgentHomePage
    | DashboardPage
    | LeaderboardPage
    | FrontendLeaderboardPage
    | ManifestDispositionTrackerPage
    | FormPage
    | JourneyProfilePage
    | HostedPage;

function isManifestListPage(page: ManifestPage): page is ManifestListPage {
    return isNonListPageKind(pageKind(page)) !== true && "filters" in page && page.filters != null;
}

/** Architect JSON shape for workspace UI config (from `workspace_ui_config.configuration`). */
export type WorkspaceConfigManifest = {
    eventTemplates: Array<{id: Uuid; displayName: string}>;
    eventContainerTemplates: Array<{id: Uuid; displayName: string; provideDisposition?: boolean}>;
    scaffold: Scaffold;
    pages: Record<string, ManifestPage>;
    components?: Record<string, ConfigComponent>;
    forms: Record<string, Form>;
    ctas: Record<string, Cta>;
    columnMapping: ColumnMapping;
    resolvers: Record<Uuid, Resolver>;
    iconRegistry?: Record<Uuid, {displayName: string; name: string}>;
    transfer?: WorkspaceTransferUiConfig;
};

/** Apply runtime defaults (e.g. rolling date filters). Identity stays on the path / capability row. */
function withWorkspaceScopedDefaults(config: WorkspaceConfigManifest): WorkspaceConfigManifest {
    const withTransfer = mergeTransferModuleIntoManifest(config);
    const from = oneMonthAgoIso();
    const to = new Date().toISOString();
    const createdAtFieldIds = new Set(
        Object.entries(withTransfer.columnMapping)
            .filter(([, entry]) => entry.type === "fixedColumn" && entry.columnName === "createdAt")
            .map(([fieldId]) => String(fieldId)),
    );
    for (const page of Object.values(withTransfer.pages)) {
        if (isManifestListPage(page) !== true) {
            continue;
        }
        const listPage = page as unknown as ManifestListPage;
        const dateRangeFieldIds = listPage.filters.dateRangeFieldId == null
            ? createdAtFieldIds
            : new Set([String(listPage.filters.dateRangeFieldId)]);
        listPage.filters.defaultApplied = listPage.filters.defaultApplied.map((filter: FilterItem) => {
            if (dateRangeFieldIds.has(String(filter.fieldId)) !== true) {
                return filter;
            }
            if (filter.type === "singleValue" && filter.operator === "greaterThanEqualTo") {
                return {...filter, value: from};
            }
            if (filter.type === "singleValue" && filter.operator === "lessThanEqualTo") {
                return {...filter, value: to};
            }
            return filter;
        });
    }
    return withTransfer;
}

function missingWorkspaceConfigError(workspaceId: Uuid): Error {
    return new Error(
        `No workspace_ui_config for workspace ${workspaceId}. Provision/enable it in Pluto.`,
    );
}

/**
 * In-memory manifests hydrated from `workspace_ui_config`.
 * Map key is always the path / DB `workspace_id` — never read from config JSON.
 */
const uiConfigManifestByWorkspaceId = new Map<string, WorkspaceConfigManifest>();

/** Config JSON only needs pages + columnMapping; identity comes from the path / DB row. */
function isWorkspaceConfigDocument(value: unknown): value is WorkspaceConfigManifest {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const config = value as {pages?: unknown; columnMapping?: unknown};
    return (
        typeof config.pages === "object" &&
        config.pages !== null &&
        typeof config.columnMapping === "object" &&
        config.columnMapping !== null
    );
}

/** Drop legacy document identity if still present in older config JSON. */
function stripLegacyConfigIdentity(value: WorkspaceConfigManifest): WorkspaceConfigManifest {
    const {
        id: _id,
        workspaceId: _workspaceId,
        ...sections
    } = value as WorkspaceConfigManifest & {id?: Uuid; workspaceId?: Uuid};
    return sections;
}

function isWorkspaceUiConfigConfiguration(
    value: unknown,
): value is WorkspaceUiConfigConfiguration {
    return isWorkspaceConfigDocument(value);
}

/** Clear in-memory hydrated config for this workspace (and scrub any leftover localStorage drafts). */
export function clearWorkspaceConfigStorage(workspaceId: Uuid): void {
    uiConfigManifestByWorkspaceId.delete(workspaceId);
    if (typeof window === "undefined") {
        return;
    }
    const suffix = `.${workspaceId}`;
    const keysToRemove: Array<string> = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (
            key !== null &&
            key.startsWith("workspace.config.") &&
            key.endsWith(suffix)
        ) {
            keysToRemove.push(key);
        }
    }
    for (const key of keysToRemove) {
        window.localStorage.removeItem(key);
    }
}

/** Backfill `*When` constraints from defaults when a stale draft saved them as null. */
function mergeFormFieldConstraintsFromFallback(storedField: FormField, fallbackField: FormField): FormField {
    const fallbackUsesStaticRequired =
        fallbackField.isRequired === true && fallbackField.requiredWhen === null;
    return {
        ...storedField,
        isRequired: fallbackUsesStaticRequired === true ? true : storedField.isRequired,
        requiredWhen:
            fallbackUsesStaticRequired === true
                ? null
                : storedField.requiredWhen ?? fallbackField.requiredWhen ?? null,
        readonlyWhen: storedField.readonlyWhen ?? fallbackField.readonlyWhen ?? null,
        visibleWhen: storedField.visibleWhen ?? fallbackField.visibleWhen ?? null,
    };
}

function mergeFormSectionsFromFallback(
    fallbackSections: Form["layout"]["sections"],
    storedSections: Form["layout"]["sections"],
): Form["layout"]["sections"] {
    const fallbackSectionById = new Map(
        fallbackSections.map((section) => {
            return [String(section.id), section] as const;
        }),
    );
    return storedSections.map((storedSection) => {
        const fallbackSection = fallbackSectionById.get(String(storedSection.id));
        if (
            fallbackSection === undefined ||
            formSectionHasFieldRows(storedSection) !== true ||
            formSectionHasFieldRows(fallbackSection) !== true
        ) {
            return storedSection;
        }
        const fallbackFieldById = new Map(
            fallbackSection.fieldRows.flat().map((field) => {
                return [String(field.fieldId), field] as const;
            }),
        );
        const mergedFieldRows = storedSection.fieldRows.map((storedRow) => {
            return storedRow.map((storedField) => {
                const fallbackField = fallbackFieldById.get(String(storedField.fieldId));
                if (fallbackField === undefined) {
                    return storedField;
                }
                return mergeFormFieldConstraintsFromFallback(storedField, fallbackField);
            });
        });
        return {
            ...storedSection,
            fieldRows: mergedFieldRows,
        };
    });
}

type MutatingForm = Extract<Form, {mode: Exclude<Form["mode"], "view">}>;

function mergeFormSubmitFromFallback(
    storedForm: MutatingForm,
    fallbackForm: MutatingForm,
): MutatingForm["submit"] {
    const storedSubmit = storedForm.submit;
    const fallbackSubmit = fallbackForm.submit;
    return {
        ...storedSubmit,
        gateSubmitUntilRequiredFieldsComplete:
            storedSubmit.gateSubmitUntilRequiredFieldsComplete ??
            fallbackSubmit.gateSubmitUntilRequiredFieldsComplete ??
            null,
        incompleteSubmitTooltip:
            storedSubmit.incompleteSubmitTooltip ?? fallbackSubmit.incompleteSubmitTooltip ?? null,
    };
}

function mergeStoredFormsWithFallback(
    fallbackForms: Record<string, Form>,
    storedForms: Record<string, Form>,
): Record<string, Form> {
    const merged: Record<string, Form> = {
        ...fallbackForms,
        ...storedForms,
    };
    for (const [formId, storedForm] of Object.entries(storedForms)) {
        const fallbackForm = fallbackForms[formId];
        if (fallbackForm === undefined) {
            continue;
        }
        if (storedForm.mode === "view" || fallbackForm.mode === "view") {
            merged[formId] = {
                ...storedForm,
                globalConstraints: storedForm.globalConstraints ?? fallbackForm.globalConstraints,
                layout: {
                    ...storedForm.layout,
                    sections: mergeFormSectionsFromFallback(
                        fallbackForm.layout.sections,
                        storedForm.layout.sections,
                    ),
                },
            };
            continue;
        }
        merged[formId] = {
            ...storedForm,
            globalConstraints: storedForm.globalConstraints ?? fallbackForm.globalConstraints,
            layout: {
                ...storedForm.layout,
                sections: mergeFormSectionsFromFallback(fallbackForm.layout.sections, storedForm.layout.sections),
            },
            submit: mergeFormSubmitFromFallback(storedForm, fallbackForm),
        } as Form;
    }
    return merged;
}

/** Merge newly supplied default pages and CTAs into a local manifest draft. */
function migrateStoredConfig(stored: WorkspaceConfigManifest, fallback: WorkspaceConfigManifest): WorkspaceConfigManifest {
    // Corrupt drafts (forms wiped / form defs stuffed into pages) → replace.
    const storedForms = (stored as Partial<WorkspaceConfigManifest>).forms;
    if (storedForms === undefined || storedForms === null || Object.keys(storedForms).length === 0) {
        return fallback;
    }
    const storedDraft = stored as Partial<WorkspaceConfigManifest>;
    const storedCtas = storedDraft.ctas ?? {};

    return {
        ...fallback,
        ...stored,
        eventTemplates: storedDraft.eventTemplates ?? fallback.eventTemplates,
        eventContainerTemplates: storedDraft.eventContainerTemplates ?? fallback.eventContainerTemplates,
        // Always take scaffold from defaults so sidebar targets (e.g. disposition
        // tracker → page) stay aligned when page types are added.
        scaffold: fallback.scaffold,
        forms: mergeStoredFormsWithFallback(fallback.forms, storedForms),
        columnMapping: {
            ...fallback.columnMapping,
            ...(storedDraft.columnMapping ?? {}),
        },
        resolvers: {
            ...fallback.resolvers,
            ...(storedDraft.resolvers ?? {}),
        },
        ctas: {
            ...fallback.ctas,
            ...storedCtas,
            ...Object.fromEntries(
                Object.entries(fallback.ctas).map(([id, defaultCta]) => {
                    const storedCta = storedCtas[id];
                    const mergedTableAction = isCtaTableAction(storedCta?.tableAction)
                        ? storedCta.tableAction
                        : isCtaTableAction(defaultCta.tableAction)
                          ? defaultCta.tableAction
                          : null;
                    const isCreateDropdownCta =
                        defaultCta.action.type === "dropdown" &&
                        (defaultCta.icon === "create" ||
                            defaultCta.label.trim().toLowerCase() === "create");
                    return [
                        id,
                        {
                            ...defaultCta,
                            ...storedCta,
                            tableAction: mergedTableAction,
                            disability: storedCta?.disability ?? defaultCta.disability,
                            inVisibility: storedCta?.inVisibility ?? defaultCta.inVisibility,
                            ...(storedCta?.placements === undefined && defaultCta.placements !== undefined
                                ? {placements: defaultCta.placements}
                                : {}),
                            // Header Create menu membership stays authored in defaults (e.g. drop Invite user).
                            ...(isCreateDropdownCta === true || storedCta?.action === undefined
                                ? {action: defaultCta.action}
                                : {}),
                        },
                    ];
                }),
            ),
        },
        pages: Object.fromEntries(
            Object.entries(fallback.pages).map(([pageId, fallbackPage]) => {
                const storedPage = stored.pages[pageId];
                if (storedPage === undefined) {
                    return [pageId, fallbackPage];
                }
                if (isManifestListPage(fallbackPage) !== true || isManifestListPage(storedPage) !== true) {
                    const keepStored = isNonListPageKind(pageKind(storedPage)) === true;
                    return [pageId, keepStored === true ? storedPage : fallbackPage];
                }
                const fallbackListPage = fallbackPage as unknown as ManifestListPage;
                const storedListPage = storedPage as unknown as ManifestListPage;
                // `toolbarCtaIds: []` is intentional (hide Create). Only backfill
                // `toolbarMoreCtaIds` when a stale draft wiped the ⋮ menu.
                const storedToolbarMoreCtaIds = storedListPage.viewEngine.toolbarMoreCtaIds;
                const toolbarMoreCtaIds =
                    storedToolbarMoreCtaIds.length > 0
                        ? storedToolbarMoreCtaIds
                        : fallbackListPage.viewEngine.toolbarMoreCtaIds;
                const storedJourneyScope = storedListPage.viewEngine.journeyScope;
                const storedScopeFilter = storedListPage.viewEngine.scopeFilter;
                const storedViews = storedListPage.viewEngine.views;
                const fallbackViews = fallbackListPage.viewEngine.views;
                const storedViewTypes = new Set(storedViews.map((view) => view.type));
                // Stale drafts may drop newer view types (e.g. focus); keep fallback entries for missing types.
                const views = [
                    ...storedViews,
                    ...fallbackViews.filter((view) => storedViewTypes.has(view.type) !== true),
                ];
                return [
                    pageId,
                    {
                        ...fallbackListPage,
                        ...storedListPage,
                        viewEngine: {
                            ...fallbackListPage.viewEngine,
                            ...storedListPage.viewEngine,
                            views,
                            toolbarCtaIds: storedListPage.viewEngine.toolbarCtaIds,
                            toolbarMoreCtaIds,
                            journeyScope:
                                storedJourneyScope === null ||
                                storedJourneyScope === undefined ||
                                Array.isArray(
                                    (storedJourneyScope as {options?: unknown}).options,
                                ) !== true
                                    ? fallbackListPage.viewEngine.journeyScope
                                    : storedJourneyScope,
                            scopeFilter:
                                storedScopeFilter === null ||
                                storedScopeFilter === undefined ||
                                typeof (storedScopeFilter as {drawerTitle?: unknown}).drawerTitle !==
                                    "string" ||
                                Array.isArray((storedScopeFilter as {options?: unknown}).options) !==
                                    true
                                    ? fallbackListPage.viewEngine.scopeFilter
                                    : storedScopeFilter,
                        },
                    },
                ];
            }),
        ),
    };
}

/**
 * Hydrate pages/scaffold from enabled `workspace_ui_config`.
 * Missing config must not wipe an already-hydrated manifest.
 * Call from workspace route beforeLoad once UI configs are fetched.
 */
export function hydrateWorkspaceConfigFromUiConfigs(
    workspaceId: Uuid,
    workspaceUiConfigs: ReadonlyArray<WorkspaceUiConfig>,
): void {
    const configuration = getOptionalWorkspaceUiConfigConfiguration(workspaceUiConfigs);
    if (configuration === null) {
        return;
    }
    setWorkspaceConfigFromUiConfig(workspaceId, configuration);
}

export function setWorkspaceConfigFromUiConfig(
    workspaceId: Uuid,
    configuration: WorkspaceUiConfigConfiguration | null,
): void {
    if (configuration === null) {
        return;
    }
    if (isWorkspaceUiConfigConfiguration(configuration) !== true) {
        return;
    }
    const manifest = withWorkspaceScopedDefaults(
        stripLegacyConfigIdentity(structuredClone(configuration) as WorkspaceConfigManifest),
    );
    uiConfigManifestByWorkspaceId.set(workspaceId, manifest);
}

export function loadOrCreateWorkspaceConfigManifest(workspaceId: Uuid): WorkspaceConfigManifest {
    const fromUiConfig = uiConfigManifestByWorkspaceId.get(workspaceId);
    if (fromUiConfig !== undefined) {
        return fromUiConfig;
    }
    throw missingWorkspaceConfigError(workspaceId);
}

/** Re-apply hydrated workspace_ui_config (clears any leftover local drafts). */
export function resetWorkspaceConfig(workspaceId: Uuid): WorkspaceConfig {
    const fromUiConfig = uiConfigManifestByWorkspaceId.get(workspaceId);
    if (fromUiConfig === undefined) {
        throw missingWorkspaceConfigError(workspaceId);
    }
    // Scrub legacy localStorage keys only; keep the in-memory backend hydrate.
    if (typeof window !== "undefined") {
        const suffix = `.${workspaceId}`;
        const keysToRemove: Array<string> = [];
        for (let index = 0; index < window.localStorage.length; index += 1) {
            const key = window.localStorage.key(index);
            if (
                key !== null &&
                key.startsWith("workspace.config.") &&
                key.endsWith(suffix)
            ) {
                keysToRemove.push(key);
            }
        }
        for (const key of keysToRemove) {
            window.localStorage.removeItem(key);
        }
    }
    const refreshed = withWorkspaceScopedDefaults(structuredClone(fromUiConfig));
    uiConfigManifestByWorkspaceId.set(workspaceId, refreshed);
    return adaptManifestToRuntime(refreshed);
}

// ---------------------------------------------------------------------------
// Runtime adapter: architect manifest pages → normalized ListPage for the harness.
// ---------------------------------------------------------------------------

function resolveListPageDataSourceId(
    page: ManifestListPage,
    fallbackKind: DataSourceKind,
): Uuid {
    if (page.dataSourceId !== undefined) {
        return page.dataSourceId;
    }
    return fallbackKind as Uuid;
}

function resolvePageDefaultSort(page: ManifestListPage): Array<SortItem> {
    if (page.defaultSort !== undefined) {
        return page.defaultSort;
    }
    return page.dataSource?.defaultSort ?? [];
}

function resolveEventContainerTemplateId(
    page: ManifestListPage,
    manifest: WorkspaceConfigManifest,
): Uuid {
    if (page.eventContainerTemplateId !== undefined) {
        return page.eventContainerTemplateId;
    }
    const first = manifest.eventContainerTemplates[0]?.id;
    if (first === undefined) {
        throw new Error(`Page "${page.id}" needs eventContainerTemplateId (or manifest.eventContainerTemplates).`);
    }
    return first;
}

function isTuffexCataloguePageKind(kind: string): boolean {
    return (
        kind === PageType.TuffexProduct ||
        kind === PageType.TuffexProductCategory ||
        kind === PageType.TuffexProductType ||
        kind === PageType.TuffexProductBrand ||
        kind === PageType.TuffexProductReviewQueue
    );
}

function pageTypeToDefaultDataSourceKind(kind: string): DataSourceKind {
    if (kind === PageType.EventWithEventContainer) {
        return DataSourceKind.EventWithEventContainer;
    }
    if (kind === PageType.EventContainerWithEvents) {
        return DataSourceKind.EventContainerWithEvent;
    }
    if (kind === PageType.AgentView) {
        return DataSourceKind.AgentViewDayPlan;
    }
    if (kind === PageType.DuluxMyDayPlanPage) {
        return DataSourceKind.DuluxAgentViewDayPlan;
    }
    if (isTuffexCataloguePageKind(kind) === true) {
        return DataSourceKind.TuffexProduct;
    }
    return DataSourceKind.Event;
}

function normalizeFieldOptions(
    raw:
        | Record<
              string,
              {
                  label: string;
                  backgroundColorToken?: string | null;
                  textColorToken?: string | null;
                  borderColorToken?: string | null;
              }
          >
        | null
        | undefined,
): Record<string, FieldOption> | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    const out: Record<string, FieldOption> = {};
    for (const [key, option] of Object.entries(raw)) {
        out[key] = {
            label: option.label,
            backgroundColorToken: option.backgroundColorToken ?? null,
            textColorToken: option.textColorToken ?? null,
            borderColorToken: option.borderColorToken ?? null,
        };
    }
    return out;
}

function normalizeManifestField(field: ManifestFieldRaw): ConfigFieldType {
    const formatter = (field.formatter ?? field.customFormatter ?? "text") as FieldFormatter;
    const base = {
        id: field.id,
        label: field.label,
        resolverId: field.resolverId ?? null,
        defaultValue: field.defaultValue ?? null,
        isVisible: field.isVisible ?? true,
        isReadOnly: field.isReadOnly ?? false,
        isRequired: field.isRequired ?? false,
        requiredWhen: field.requiredWhen ?? null,
        readonlyWhen: field.readonlyWhen ?? null,
        visibleWhen: field.visibleWhen ?? null,
        errorMessage: field.errorMessage ?? null,
        validation: field.validation ?? null,
        ...(typeof field.emptyLabel === "string" && field.emptyLabel !== ""
            ? {emptyLabel: field.emptyLabel}
            : {}),
    };
    if (formatter === "disposition") {
        if (field.optionSource === undefined) {
            throw new Error(`Disposition field ${String(field.id)} must declare optionSource`);
        }
        return {
            ...base,
            formatter: formatter,
            optionSource: field.optionSource,
        };
    }
    if (formatter === "select" && field.optionSource !== undefined) {
        return {
            ...base,
            formatter: formatter,
            optionSource: field.optionSource,
        };
    }
    if (formatter === "select") {
        return {
            ...base,
            formatter: formatter,
            options: normalizeFieldOptions(field.options),
        };
    }
    return {
        ...base,
        formatter: formatter,
    };
}

function normalizeProfilePanel(raw: ManifestListPage["profilePanel"]): ConfigAgentViewProfilePanel {
    if (raw === undefined) {
        throw new Error("Agent view page is missing profilePanel.");
    }
    return {
        viewFormId: raw.viewFormId,
        editFormId: raw.editFormId ?? null,
        headerTitle: raw.headerTitle ?? null,
        tabs: raw.tabs ?? null,
        eventContainerSourceLabel: raw.eventContainerSourceLabel ?? null,
        footerToolbarCtaIds: raw.footerToolbarCtaIds,
        footerMoreCtaIds: raw.footerMoreCtaIds,
        footerAppearance:
            raw.footerAppearance === undefined || raw.footerAppearance === null
                ? null
                : {
                      iconColorToken: raw.footerAppearance.iconColorToken ?? null,
                      textColorToken: raw.footerAppearance.textColorToken ?? null,
                  },
    };
}

function adaptScopeFilter(raw: unknown): PageViewEngine["scopeFilter"] {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (
        typeof raw !== "object" ||
        Array.isArray((raw as {options?: unknown}).options) !== true ||
        typeof (raw as {drawerTitle?: unknown}).drawerTitle !== "string" ||
        ((raw as {selectionMode?: unknown}).selectionMode !== "single" &&
            (raw as {selectionMode?: unknown}).selectionMode !== "multi")
    ) {
        throw new Error(
            'scopeFilter must use { drawerTitle, selectionMode: "single"|"multi", options: [{ id, filterKey, label, type, filterType }] }',
        );
    }
    const scopeFilter = raw as NonNullable<PageViewEngine["scopeFilter"]>;
    return {
        drawerTitle: scopeFilter.drawerTitle,
        selectionMode: scopeFilter.selectionMode,
        options: scopeFilter.options.map((option) => ({
            id: option.id,
            filterKey: option.filterKey,
            label: option.label,
            type: option.type,
            filterType: option.filterType,
            ...(option.defaultValue !== undefined ? {defaultValue: option.defaultValue} : {}),
        })),
    };
}

function normalizePageView(view: PageView): PageView {
    if (view.type === "list") {
        return {
            type: "list",
            cardLayout: normalizeCardLayout(view.cardLayout),
        };
    }
    if (view.type === "kanban") {
        return {
            type: "kanban",
            statusField: view.statusField,
            statusGroups: view.statusGroups.map((group) => ({
                id: group.id,
                label: group.label,
                values: group.values,
                backgroundColorToken: group.backgroundColorToken ?? null,
                textColorToken: group.textColorToken ?? null,
                ...(typeof group.timingLabel === "string" && group.timingLabel !== ""
                    ? {timingLabel: group.timingLabel}
                    : {}),
            })),
            cardLayout: normalizeCardLayout(view.cardLayout),
            layout:
                view.layout === null || view.layout === undefined
                    ? null
                    : {
                          columnMinWidthPx: view.layout.columnMinWidthPx ?? null,
                          columnGapPx: view.layout.columnGapPx ?? null,
                          mobileCardsGridTemplateColumns: view.layout.mobileCardsGridTemplateColumns ?? null,
                          hideColumnPin: view.layout.hideColumnPin ?? null,
                      },
            columnHeaderStyle:
                view.columnHeaderStyle === null || view.columnHeaderStyle === undefined
                    ? null
                    : {
                          backgroundColorToken: view.columnHeaderStyle.backgroundColorToken ?? null,
                          textColorToken: view.columnHeaderStyle.textColorToken ?? null,
                          headerBorderRadius: view.columnHeaderStyle.headerBorderRadius ?? null,
                          headerBorderColor: view.columnHeaderStyle.headerBorderColor ?? null,
                          headerBorderWidth: view.columnHeaderStyle.headerBorderWidth ?? null,
                      },
        };
    }
    return view;
}

function adaptJourneyScope(raw: unknown): PageViewEngine["journeyScope"] {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (
        typeof raw !== "object" ||
        Array.isArray((raw as {options?: unknown}).options) !== true ||
        typeof (raw as {statusColumnId?: unknown}).statusColumnId !== "string" ||
        typeof (raw as {defaultApplied?: unknown}).defaultApplied !== "string"
    ) {
        throw new Error(
            "journeyScope must use options shape: { title, statusColumnId, defaultApplied, options }",
        );
    }
    const scope = raw as NonNullable<PageViewEngine["journeyScope"]>;
    return {
        title: scope.title ?? "Display by",
        statusColumnId: scope.statusColumnId,
        defaultApplied: scope.defaultApplied,
        options: scope.options,
    };
}

function adaptListPage(page: ManifestListPage, manifest: WorkspaceConfigManifest): ListPage | DuluxMyDayPlanPage {
    const kind = pageKind(page);
    const dataSourceId = resolveListPageDataSourceId(page, pageTypeToDefaultDataSourceKind(kind));
    const defaultSort = resolvePageDefaultSort(page);

    const listPageBase = {
        allowedUserTemplateIds: page.allowedUserTemplateIds,
        readOnly: page.readOnly,
        id: page.id,
        title: page.title,
        persistInLocalStorage: page.persistInLocalStorage === true,
        archivedPageId: page.archivedPageId ?? null,
        parentActivePageId: page.parentActivePageId ?? null,
        dispositionProviderEventContainerTemplateId: page.dispositionProviderEventContainerTemplateId,
        dataSourceId,
        defaultSort,
        filters: {
            defaultApplied: page.filters.defaultApplied,
            dateRangeFieldId: page.filters.dateRangeFieldId,
            visibleFieldIds: page.filters.visibleFieldIds,
            userCanClear: page.filters.userCanClear ?? true,
            defaultCreatedAtMonthFilter: page.filters.defaultCreatedAtMonthFilter ?? null,
            hideCreatedAtDateRangeFilter: page.filters.hideCreatedAtDateRangeFilter ?? null,
        },
        mandatoryFilters: page.mandatoryFilters ?? null,
        fields: page.fields.map(normalizeManifestField),
        viewEngine: {
            toolbarCtaIds: page.viewEngine.toolbarCtaIds ?? [],
            toolbarMoreCtaIds: page.viewEngine.toolbarMoreCtaIds ?? [],
            defaultDesktopView: page.viewEngine.defaultDesktopView,
            defaultMobileView: page.viewEngine.defaultMobileView,
            searchFields: page.viewEngine.searchFields ?? [],
            journeyScope: adaptJourneyScope(page.viewEngine.journeyScope),
            scopeFilter: adaptScopeFilter(page.viewEngine.scopeFilter),
            inQueueAssignmentFilter: page.viewEngine.inQueueAssignmentFilter ?? null,
            views: page.viewEngine.views.map((view) => normalizePageView(view as PageView)),
        },
    };

    if (kind === PageType.AgentView) {
        return {
            ...listPageBase,
            type: PageType.AgentView,
            profilePanel: normalizeProfilePanel(page.profilePanel),
        } as AgentViewPage;
    }
    if (kind === PageType.DuluxMyDayPlanPage) {
        if (page.domainAggregateTemplateId === undefined || page.timeliness === undefined) {
            throw new Error(`Page "${page.id}" is missing Dulux agent view configuration.`);
        }
        return {
            ...listPageBase,
            domainAggregateTemplateId: page.domainAggregateTemplateId,
            profilePanel: normalizeProfilePanel(page.profilePanel),
            timeliness: page.timeliness,
            type: PageType.DuluxMyDayPlanPage,
        } as DuluxMyDayPlanPage;
    }
    if (kind === PageType.EventContainerWithEvents) {
        return {
            ...listPageBase,
            type: PageType.EventContainerWithEvents,
            eventContainerTemplateId: resolveEventContainerTemplateId(page, manifest),
        } as EventContainerWithEventsPage;
    }
    if (kind === PageType.EventWithEventContainer) {
        const eventTemplateId = page.eventTemplateId ?? page.dataSource?.eventTemplateId;
        if (eventTemplateId === undefined) {
            throw new Error(`Page "${page.id}" is missing eventTemplateId.`);
        }
        return {
            ...listPageBase,
            type: kind,
            eventTemplateId,
        } as EventWithEventContainerPage;
    }
    if (isTuffexCataloguePageKind(kind) === true) {
        return {
            ...listPageBase,
            type: kind,
        } as ListPage;
    }
    const eventTemplateId = page.eventTemplateId ?? page.dataSource?.eventTemplateId;
    if (eventTemplateId === undefined) {
        throw new Error(`Page "${page.id}" is missing eventTemplateId.`);
    }
    return {
        ...listPageBase,
        type: PageType.EventPage,
        eventTemplateId,
    } as EventPage;
}

function normalizeEventsBar(eventsBar: CardEventsBar | null | undefined): CardEventsBar | null {
    if (eventsBar === null || eventsBar === undefined) {
        return null;
    }
    return {
        label: eventsBar.label,
        iconId: eventsBar.iconId ?? null,
        isCollapsible: eventsBar.isCollapsible === true,
        isCollapsed: eventsBar.isCollapsed === true,
        items: eventsBar.items.map((item) => ({
            fieldId: item.fieldId,
            label: item.label,
        })),
    };
}

function normalizeDetailCell(cell: CardDetailCell): CardDetailCell {
    if (cell.type === "SingleColumn") {
        return {
            type: "SingleColumn",
            fieldId: cell.fieldId,
            label: cell.label,
            iconId: cell.iconId ?? null,
        };
    }
    return {
        type: "MultipleColumn",
        fieldIds: cell.fieldIds,
        separator: cell.separator,
        label: cell.label,
        iconId: cell.iconId ?? null,
    };
}

function normalizeCardLayout(layout: CardLayout): CardLayout {
    return {
        title: layout.title,
        subtitle: layout.subtitle,
        status: layout.status,
        progress: layout.progress,
        dispositionBand: layout.dispositionBand,
        actionBar: layout.actionBar,
        meta: layout.meta.map((item) => {
            if (item.presentation === "pill") {
                return {
                    presentation: "pill" as const,
                    fieldId: item.fieldId,
                    iconId: item.iconId ?? null,
                    label: item.label,
                    backgroundColor: item.backgroundColor ?? null,
                    textColor: item.textColor ?? null,
                    iconColor: item.iconColor ?? null,
                };
            }
            return {
                presentation: "simple" as const,
                fieldId: item.fieldId,
                iconId: item.iconId ?? null,
                label: item.label,
            };
        }),
        details: layout.details.map((row) => row.map(normalizeDetailCell)),
        primaryCtaIds: layout.primaryCtaIds,
        moreCtaIds: layout.moreCtaIds,
        actionSlot: layout.actionSlot,
        showDividerBeforeDetails: layout.showDividerBeforeDetails === true,
        showAssignedOu: layout.showAssignedOu ?? null,
        showDispositionChip: layout.showDispositionChip ?? null,
        eventsBar: normalizeEventsBar(layout.eventsBar),
        showPhoneDial: layout.showPhoneDial ?? null,
        ...(layout.showOverflowCtas === true ? {showOverflowCtas: true} : {}),
    };
}

function sectionUsesDispositionPath(section: Form["layout"]["sections"][number]): boolean {
    if (section.kind === "notes") {
        return false;
    }
    return section.fieldRows.some((fieldRow) =>
        fieldRow.some((formField) => formField.presentation === "dispositionPath"),
    );
}

const KNOWN_FORM_MODES: ReadonlySet<Form["mode"]> = new Set([
    "create",
    "update",
    "view",
    "log",
    "schedule",
    "scheduleModal",
    "book",
    "cancel",
    "archive",
    "disposition",
]);

/** Repair stale drafts that stored a disposition field UUID as `mode` / `kind`. */
function normalizeForm(form: Form): Form {
    const sections = form.layout.sections.map((section) => {
        if (section.kind === "disposition" || section.kind === "notes" || sectionUsesDispositionPath(section) !== true) {
            return section;
        }
        return {...section, kind: "disposition" as const};
    });
    const hasDispositionSection = sections.some((section) => section.kind === "disposition");
    const modeIsKnown = KNOWN_FORM_MODES.has(form.mode);
    const mode: Form["mode"] =
        modeIsKnown === true
            ? form.mode
            : hasDispositionSection === true
              ? "disposition"
              : "update";
    return {
        ...form,
        mode,
        layout: {
            ...form.layout,
            sections,
        },
    } as Form;
}

function isFlatNavArray(value: unknown): value is SidebarType {
    return Array.isArray(value);
}

function isScaffoldAppArray(value: unknown): value is Array<ScaffoldApp> {
    if (Array.isArray(value) !== true) {
        return false;
    }
    if (value.length === 0) {
        return true;
    }
    const firstEntry = value[0];
    return (
        firstEntry !== null &&
        typeof firstEntry === "object" &&
        "id" in firstEntry &&
        "label" in firstEntry &&
        "icon" in firstEntry
    );
}

function isScaffoldBottomBarArray(value: unknown): value is Array<ScaffoldBottomBar> {
    if (Array.isArray(value) !== true) {
        return false;
    }
    if (value.length === 0) {
        return true;
    }
    const firstEntry = value[0];
    return (
        firstEntry !== null &&
        typeof firstEntry === "object" &&
        "id" in firstEntry &&
        "items" in firstEntry
    );
}

function isScaffoldAppRecord(sidebar: unknown): sidebar is Record<Uuid, ScaffoldApp> {
    if (typeof sidebar !== "object" || sidebar === null || Array.isArray(sidebar) === true) {
        return false;
    }
    const firstEntry = Object.values(sidebar)[0];
    return (
        firstEntry !== undefined &&
        typeof firstEntry === "object" &&
        firstEntry !== null &&
        "modules" in firstEntry
    );
}

function coerceSidebar(sidebar: unknown): Array<ScaffoldApp> {
    if (isScaffoldAppArray(sidebar) === true) {
        return sidebar;
    }
    if (isScaffoldAppRecord(sidebar) === true) {
        return Object.values(sidebar);
    }
    return [];
}

function coerceBottomBar(bottomBar: unknown): Array<ScaffoldBottomBar> {
    if (isScaffoldBottomBarArray(bottomBar) === true) {
        return bottomBar;
    }
    if (isFlatNavArray(bottomBar) === true) {
        return [];
    }
    if (typeof bottomBar === "object" && bottomBar !== null) {
        return Object.entries(bottomBar).map(([id, items]) => ({
            id: id as Uuid,
            items: Array.isArray(items) === true ? (items as Array<NavItem>) : [],
        }));
    }
    return [];
}

function isConfigUuid(value: unknown): value is Uuid {
    return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) === true;
}

function normalizeRoleScaffold(roleScaffold: {
    entryPageId?: unknown;
    sidebar?: unknown;
    header?: ScaffoldByUserTemplate["header"];
    bottomBar?: unknown;
}): ScaffoldByUserTemplate {
    const sidebar = coerceSidebar(roleScaffold.sidebar);
    const bottomBar = coerceBottomBar(roleScaffold.bottomBar);
    const derivedEntryPageId = resolveFirstSidebarPageKey({sidebar});
    const entryPageId: Uuid = isConfigUuid(roleScaffold.entryPageId)
        ? roleScaffold.entryPageId
        : (derivedEntryPageId ?? WORKSPACE_APP_ID);
    return {
        entryPageId: entryPageId,
        sidebar: sidebar,
        header: roleScaffold.header ?? [],
        bottomBar: bottomBar,
    };
}

function normalizeScaffold(scaffold: WorkspaceConfigManifest["scaffold"]): WorkspaceConfig["scaffold"] {
    return Object.fromEntries(
        Object.entries(scaffold).map(([templateId, roleScaffold]) => {
            return [templateId, normalizeRoleScaffold(roleScaffold)];
        }),
    ) as WorkspaceConfig["scaffold"];
}

function adaptManifestToRuntime(manifest: WorkspaceConfigManifest): WorkspaceConfig {
    const pages = Object.fromEntries(
        Object.entries(manifest.pages).flatMap(([key, page]): Array<[string, WorkspacePage]> => {
            if (isNonListPageKind(pageKind(page)) === true) {
                const nonList = page as unknown as WorkspacePage;
                if (isDispositionTrackerPage(nonList) === true) {
                    const tracker = page as ManifestDispositionTrackerPage;
                    const dataSourceId =
                        tracker.dataSourceId ??
                        (DataSourceKind.EventContainerWithEvent as Uuid);
                    return [
                        [
                            key,
                            {
                                ...tracker,
                                dataSourceId,
                            } as WorkspacePage,
                        ],
                    ];
                }
                return [[key, nonList]];
            }
            if (isManifestListPage(page) !== true) {
                return [];
            }
            return [[key, adaptListPage(page as unknown as ManifestListPage, manifest)]];
        }),
    );
    const config = {
        ...manifest,
        resolvers: manifest.resolvers ?? {},
        dataSources: createBuiltinDataSourcesRecord(),
        scaffold: normalizeScaffold(manifest.scaffold),
        components: (manifest.components ?? {}) as WorkspaceConfig["components"],
        forms: Object.fromEntries(
            Object.entries(manifest.forms).map(([id, form]) => [id, normalizeForm(form)]),
        ),
        ctas: Object.fromEntries(
            Object.entries(manifest.ctas).map(([id, cta]) => [
                id,
                {
                    ...cta,
                    tableAction: isCtaTableAction(cta.tableAction) ? cta.tableAction : null,
                },
            ]),
        ) as WorkspaceConfig["ctas"],
        pages,
    } as unknown as WorkspaceConfig;

    config.columnMapping = enrichColumnMappingFormatters(config.columnMapping, config.pages);

    for (const page of Object.values(config.pages)) {
        if (isListStylePage(page) === true || isDuluxMyDayPlanPage(page) === true) {
            attachPageFieldBindings(page, config.columnMapping);
        }
    }
    return config;
}

export {
    columnMappingEntityKind,
    findFormPageByFormId,
    flattenCardDetailCells,
    isAgentHomePage,
    isCtaTableAction,
    isDashboardPage,
    isDispositionTrackerPage,
    isFormPage,
    isHostedPage,
    isJourneyProfileComponent,
    isLeaderboardPage,
    isFrontendLeaderboardPage,
    isJourneyProfilePage,
    isListStylePage,
    isUserInvitationPage,
    isUsersPage,
    parseColumnMappingEntity,
    resolveFilterItemValues,
    resolveFrontendFieldIdToColumn,
};

export function loadOrCreateWorkspaceConfig(workspaceId: Uuid): WorkspaceConfig {
    return adaptManifestToRuntime(loadOrCreateWorkspaceConfigManifest(workspaceId));
}

/** True only when an enabled `workspace_ui_config` was hydrated for this workspace. */
export function hasWorkspaceUiConfig(workspaceId: Uuid): boolean {
    return uiConfigManifestByWorkspaceId.has(workspaceId) === true;
}

export function getWorkspaceConfigManifest(workspaceId: Uuid): WorkspaceConfigManifest {
    return loadOrCreateWorkspaceConfigManifest(workspaceId);
}

export function resolveScaffoldForUserTemplate(
    scaffold: WorkspaceConfigManifest["scaffold"],
    userTemplateId: Uuid,
): ScaffoldByUserTemplate | null {
    const exact = scaffold[userTemplateId];
    if (exact !== undefined) {
        return normalizeRoleScaffold(exact);
    }
    const first = Object.values(scaffold)[0];
    if (first !== undefined) {
        return normalizeRoleScaffold(first);
    }
    return null;
}

export function loadScaffoldForWorkspace(args: {
    workspaceId: Uuid;
    userTemplateId: Uuid;
}): ScaffoldByUserTemplate | null {
    try {
        return resolveScaffoldForUserTemplate(
            getWorkspaceConfigManifest(args.workspaceId).scaffold,
            args.userTemplateId,
        );
    } catch {
        return null;
    }
}

function findManifestPageIdByTypes(
    manifest: WorkspaceConfigManifest,
    types: ReadonlyArray<string>,
): Uuid | undefined {
    for (const page of Object.values(manifest.pages)) {
        if (types.includes(page.type) === true) {
            return page.id as Uuid;
        }
    }
    return undefined;
}

/** Scaffold app ids (e.g. Ottopilot `WORKSPACE_APP_ID`) are not pages. */
function resolvePageKeyThatExistsInManifest(
    workspaceId: Uuid,
    manifest: WorkspaceConfigManifest,
    candidatePageKey: Uuid | null,
): Uuid {
    if (candidatePageKey !== null) {
        if (manifest.pages[candidatePageKey] !== undefined) {
            return candidatePageKey;
        }
        for (const page of Object.values(manifest.pages)) {
            if (page.id === candidatePageKey) {
                return page.id as Uuid;
            }
        }
    }
    const homePageId = findManifestPageIdByTypes(manifest, [
        PageType.OttokartHome,
        PageType.AgentHomePage,
        PageType.DuluxAgentHomePage,
        PageType.WorkspaceBrandingHome,
        PageType.DuluxDashboard,
        PageType.DashBoard,
    ]);
    if (homePageId !== undefined) {
        return homePageId;
    }
    const firstPage = Object.values(manifest.pages)[0];
    if (firstPage !== undefined) {
        return firstPage.id as Uuid;
    }
    throw missingWorkspaceConfigError(workspaceId);
}

export function resolveWorkspaceConfigEntry(
    workspaceId: Uuid,
    userTemplateId: Uuid,
): {pageKey: Uuid} {
    const manifest = getWorkspaceConfigManifest(workspaceId);
    const roleChrome = resolveScaffoldForUserTemplate(manifest.scaffold, userTemplateId);
    if (roleChrome === null) {
        throw missingWorkspaceConfigError(workspaceId);
    }
    const pageKey = resolvePageKeyThatExistsInManifest(workspaceId, manifest, roleChrome.entryPageId);
    return {
        pageKey: pageKey,
    };
}

export function resolveDefaultConfigPageKey(workspaceId: Uuid, userTemplateId: Uuid): Uuid {
    return resolveWorkspaceConfigEntry(workspaceId, userTemplateId).pageKey;
}

export function buildWorkspaceConfigEntryHref(workspaceId: Uuid, userTemplateId: Uuid): string {
    const entry = resolveWorkspaceConfigEntry(workspaceId, userTemplateId);
    return `/w/${workspaceId}/${entry.pageKey}`;
}

export {migrateStoredConfig, adaptManifestToRuntime};
